import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  AlertTriangle,
  CheckCircle,
  ShieldAlert,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Alert, AlertDescription } from "./ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import AdminQuotationReview from "./AdminQuotationReview";
import AdminUsers from "./AdminUsers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AdminPanel — now gated behind a real OTP-verified Blexbot session.
 *
 * Admin1 signs in through the same WhatsApp OTP flow as regular users. The
 * verified `pv_session_token` is what powers every /admin request here, so
 * cross-user drill-downs reuse the admin's live Blexbot cookies. When the
 * session expires, any /admin call returns 401 and we prompt the admin to
 * verify again via WhatsApp instead of showing stale data or an error.
 */
function AdminPanel({ onNavigateBack, onRequestReverify, onOpenUserCase }) {
  const getSessionToken = () => {
    try { return sessionStorage.getItem("pv_session_token") || ""; } catch { return ""; }
  };
  const sessionHeaders = () => ({ "X-PV-Session": getSessionToken() });

  const [adminProducts, setAdminProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newProduct, setNewProduct] = useState({
    description: "",
    maker: "",
    model: "",
    market_price: "",
    image_base64: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Centralized 401 handler — any admin call that fails auth flips the
  // panel into a "please verify again" state and routes back to Sign In.
  const handleAuthError = useCallback((err) => {
    if (err?.response?.status === 401) {
      setSessionExpired(true);
      return true;
    }
    return false;
  }, []);

  const loadAdminProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/products`, { headers: sessionHeaders() });
      setAdminProducts(response.data);
    } catch (err) {
      if (!handleAuthError(err)) setError("Failed to load products");
    }
  }, [handleAuthError]);

  useEffect(() => {
    if (!getSessionToken()) {
      setSessionExpired(true);
      return;
    }
    loadAdminProducts();
  }, [loadAdminProducts]);

  const createProduct = async () => {
    if (!newProduct.description || !newProduct.maker || !newProduct.market_price) {
      setError("Please fill in all required fields");
      return;
    }
    try {
      setLoading(true);
      await axios.post(
        `${API}/admin/products`,
        { ...newProduct, market_price: parseFloat(newProduct.market_price) },
        { headers: sessionHeaders() }
      );
      setNewProduct({ description: "", maker: "", model: "", market_price: "", image_base64: "" });
      setSuccess("Product created successfully");
      loadAdminProducts();
    } catch (err) {
      if (!handleAuthError(err)) setError("Failed to create product");
    } finally {
      setLoading(false);
    }
  };

  const updateProduct = async (productId) => {
    try {
      setLoading(true);
      await axios.put(
        `${API}/admin/products/${productId}`,
        { ...editingProduct, market_price: parseFloat(editingProduct.market_price) },
        { headers: sessionHeaders() }
      );
      setEditingProduct(null);
      setSuccess("Product updated successfully");
      loadAdminProducts();
    } catch (err) {
      if (!handleAuthError(err)) setError("Failed to update product");
    } finally {
      setLoading(false);
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      setLoading(true);
      await axios.delete(`${API}/admin/products/${productId}`, { headers: sessionHeaders() });
      setSuccess("Product deleted successfully");
      loadAdminProducts();
    } catch (err) {
      if (!handleAuthError(err)) setError("Failed to delete product");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e, isEditing = false) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError("Image size must be less than 2MB");
      return;
    }
    setUploadingImage(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      if (isEditing) setEditingProduct({ ...editingProduct, image_base64: base64String });
      else setNewProduct({ ...newProduct, image_base64: base64String });
      setUploadingImage(false);
    };
    reader.onerror = () => {
      setError("Failed to read image file");
      setUploadingImage(false);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = (isEditing = false) => {
    if (isEditing) setEditingProduct({ ...editingProduct, image_base64: null });
    else setNewProduct({ ...newProduct, image_base64: "" });
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount || 0);

  // ───────── Session-expired re-verify prompt ─────────
  if (sessionExpired) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 py-6 flex items-center space-x-4">
            <Button variant="ghost" onClick={onNavigateBack} data-testid="admin-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            <h1 className="text-2xl font-bold">Admin Panel</h1>
          </div>
        </div>
        <div className="max-w-md mx-auto pt-16">
          <Card data-testid="admin-reverify-card">
            <CardHeader className="text-center">
              <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <ShieldAlert className="w-6 h-6 text-amber-600" />
              </div>
              <CardTitle>Admin session expired</CardTitle>
              <CardDescription>
                Your Blexbot session has ended. Please verify again via WhatsApp to continue.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                onClick={() => (onRequestReverify ? onRequestReverify() : onNavigateBack())}
                data-testid="admin-reverify-btn"
                className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-full"
              >
                Verify via WhatsApp
              </Button>
              <Button variant="outline" onClick={onNavigateBack} className="w-full rounded-full">
                Back to Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={onNavigateBack} data-testid="admin-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <h1 className="text-2xl font-bold">Admin Panel</h1>
            </div>
            <Button
              variant="outline"
              onClick={onNavigateBack}
              data-testid="admin-logout-btn"
            >
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <Alert className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="mb-6">
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        {/* Users (session-powered) */}
        <AdminUsers onAuthError={handleAuthError} onOpenUserCase={onOpenUserCase} />

        {/* Quotation Review (new) */}
        <AdminQuotationReview />

        {/* Add New Product */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Add New Product</CardTitle>
            <CardDescription>
              Add a new product to the database
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <Input
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                  placeholder="Product description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Maker *</label>
                <Input
                  value={newProduct.maker}
                  onChange={(e) => setNewProduct({ ...newProduct, maker: e.target.value })}
                  placeholder="Manufacturer"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                <Input
                  value={newProduct.model}
                  onChange={(e) => setNewProduct({ ...newProduct, model: e.target.value })}
                  placeholder="Model number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Market Price *</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newProduct.market_price}
                  onChange={(e) => setNewProduct({ ...newProduct, market_price: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Image Upload Section */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Image <span className="text-gray-500 font-normal">(Optional)</span>
              </label>
              <div className="flex items-start space-x-4">
                {newProduct.image_base64 ? (
                  <div className="relative">
                    <img
                      src={newProduct.image_base64}
                      alt="Product preview"
                      className="w-24 h-24 object-cover rounded-lg border-2 border-gray-300"
                    />
                    <button
                      onClick={() => removeImage(false)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-100">
                    <span className="text-gray-400 text-xs text-center">No Image</span>
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, false)}
                    disabled={uploadingImage}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-cyan-50 file:text-cyan-700 hover:file:bg-cyan-100"
                  />
                  <p className="mt-1 text-xs text-gray-500">PNG, JPG, GIF up to 2MB</p>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <Button onClick={createProduct} disabled={loading || uploadingImage}>
                <Plus className="w-4 h-4 mr-2" />
                {uploadingImage ? "Processing Image..." : "Add Product"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Products List */}
        <Card>
          <CardHeader>
            <CardTitle>Products Database ({adminProducts.length})</CardTitle>
            <CardDescription>Manage existing products in the database</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Image</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Maker</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead>Market Price</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {adminProducts.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>
                        {editingProduct && editingProduct.id === product.id ? (
                          <div className="flex items-center space-x-2">
                            {editingProduct.image_base64 ? (
                              <div className="relative">
                                <img
                                  src={editingProduct.image_base64}
                                  alt="Product"
                                  className="w-12 h-12 object-cover rounded border"
                                />
                                <button
                                  onClick={() => removeImage(true)}
                                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                                >
                                  <X className="w-2 h-2" />
                                </button>
                              </div>
                            ) : (
                              <div className="w-12 h-12 border border-dashed border-gray-300 rounded flex items-center justify-center">
                                <span className="text-xs text-gray-400">No img</span>
                              </div>
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(e, true)}
                              className="text-xs"
                              style={{ width: "100px" }}
                            />
                          </div>
                        ) : product.image_base64 ? (
                          <img
                            src={product.image_base64}
                            alt={product.description}
                            className="w-12 h-12 object-cover rounded border"
                          />
                        ) : (
                          <div className="w-12 h-12 border border-dashed border-gray-300 rounded flex items-center justify-center">
                            <span className="text-xs text-gray-400">No image</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingProduct && editingProduct.id === product.id ? (
                          <Input
                            value={editingProduct.description}
                            onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })}
                          />
                        ) : (
                          <span className="font-medium">{product.description}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingProduct && editingProduct.id === product.id ? (
                          <Input
                            value={editingProduct.maker}
                            onChange={(e) => setEditingProduct({ ...editingProduct, maker: e.target.value })}
                          />
                        ) : (
                          product.maker
                        )}
                      </TableCell>
                      <TableCell>
                        {editingProduct && editingProduct.id === product.id ? (
                          <Input
                            value={editingProduct.model}
                            onChange={(e) => setEditingProduct({ ...editingProduct, model: e.target.value })}
                          />
                        ) : (
                          product.model
                        )}
                      </TableCell>
                      <TableCell>
                        {editingProduct && editingProduct.id === product.id ? (
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editingProduct.market_price}
                            onChange={(e) => setEditingProduct({ ...editingProduct, market_price: e.target.value })}
                          />
                        ) : (
                          <span className="font-medium text-green-600">{formatCurrency(product.market_price)}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {editingProduct && editingProduct.id === product.id ? (
                            <>
                              <Button size="sm" onClick={() => updateProduct(product.id)}>
                                <Save className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setEditingProduct(null)}>
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingProduct({ ...product })}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => deleteProduct(product.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t mt-16">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <p className="text-center text-gray-600 text-sm">
            ClaimValuator! Quickly calculate claim values with up-to-date market prices from our 1M+ product database.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default AdminPanel;
